import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import MeritScoreChart from "./MeritScoreChart";
import API from "../lib/api";

// Props:
//   application: {
//     id: string,
//     merit_score: number | null,
//     score_breakdown: { skill_match, experience, verified, portfolio } | null,
//     explanation: string | null,
//     unverified_flags: string[],
//     recruiter_notes: string | null,
//     stage_history: [{ stage, timestamp }] | null,
//     student: {
//       name, college, branch, cgpa, graduation_year, classified_role,
//       extracted_skills: string[],
//       verified_skills: [{ skill_key, status, pct }],
//       ai_summary: string | null,
//       phone: string | null,
//     }
//   } | null
//   isOpen: boolean
//   onClose: () => void
//   onSaveNote: (note: string) => void
//   isDark: boolean
//   t: theme object { glassCard, inputStyle, accent, accentDark, accentLight, accentBorder,
//                    textPrimary, textSecondary, textMuted, cardBg }

function scoreColor(score) {
  if (score >= 75) return "#16a34a";
  if (score >= 50) return "#f97316";
  return "#dc2626";
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function ApplicationDeepDive({
  application,
  isOpen,
  onClose,
  onSaveNote,
  isDark = false,
  t = {},
}) {
  const [noteText, setNoteText] = useState("");
  const [attritionRisk, setAttritionRisk] = useState(null);

  // Sync note text when application changes
  useEffect(() => {
    setNoteText(application?.recruiter_notes ?? "");
  }, [application?.id, application?.recruiter_notes]);

  useEffect(() => {
    if (!application?.id) { setAttritionRisk(null); return; }
    API.get(`/api/recruiter/applications/${application.id}/attrition-risk`)
      .then(r => setAttritionRisk(r.data)).catch(() => setAttritionRisk(null));
  }, [application?.id]);

  if (!application) return null;

  const { student = {}, merit_score, score_breakdown, explanation, unverified_flags = [], stage_history = [] } = application;

  const {
    name = "Applicant",
    college = "",
    branch = "",
    cgpa,
    graduation_year,
    classified_role = "",
    extracted_skills = [],
    verified_skills = [],
    ai_summary,
  } = student;

  const initial = (name || "A")[0].toUpperCase();

  // Verified skill keys (status === "verified")
  const verifiedSkillEntries = verified_skills.filter((vs) => vs.status === "verified");
  const verifiedKeys = new Set(verifiedSkillEntries.map((vs) => vs.skill_key?.toLowerCase()));

  // Remaining extracted skills (not verified and not in unverified_flags)
  const unverifiedFlagSet = new Set((unverified_flags || []).map((f) => f.toLowerCase()));
  const neutralSkills = extracted_skills.filter(
    (s) => !verifiedKeys.has(s.toLowerCase()) && !unverifiedFlagSet.has(s.toLowerCase())
  );

  const cardBg = t.cardBg || (isDark ? "rgba(255,255,255,0.05)" : "#ffffff");
  const textPrimary = t.textPrimary || (isDark ? "#ffffff" : "#0a0a0f");
  const textSecondary = t.textSecondary || (isDark ? "rgba(255,255,255,0.5)" : "#64748b");
  const textMuted = t.textMuted || (isDark ? "rgba(255,255,255,0.3)" : "#94a3b8");
  const sectionBorder = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)";
  const inputBg = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.04)";
  const inputBorder = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)";
  const blockquoteBg = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)";

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="max-w-[480px] p-0 gap-0 rounded-xl overflow-hidden"
        style={{
          maxWidth: 480,
          background: cardBg,
          color: textPrimary,
          border: `1px solid ${sectionBorder}`,
        }}
        data-testid="application-deep-dive"
      >
        {/* ── Header ── */}
        <DialogHeader className="px-5 pt-5 pb-4" style={{ borderBottom: `1px solid ${sectionBorder}` }}>
          <div className="flex items-start gap-3 pr-6">
            {/* Avatar */}
            <div
              className="flex items-center justify-center rounded-full shrink-0 font-bold text-lg text-white"
              style={{ width: 48, height: 48, background: "#f97316" }}
              aria-hidden="true"
            >
              {initial}
            </div>

            <div className="flex-1 min-w-0">
              <DialogTitle
                className="text-base font-semibold leading-tight truncate"
                style={{ color: textPrimary }}
              >
                {name}
              </DialogTitle>

              {classified_role && (
                <span
                  className="inline-block text-xs font-medium px-2 py-0.5 rounded-full mt-1"
                  style={{ background: "rgba(249,115,22,0.12)", color: "#f97316" }}
                >
                  {classified_role}
                </span>
              )}

              <p className="text-xs mt-1 truncate" style={{ color: textSecondary }}>
                {[college, branch].filter(Boolean).join(" · ")}
                {cgpa ? ` · CGPA ${cgpa}` : ""}
                {graduation_year ? ` · ${graduation_year}` : ""}
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* ── Scrollable body ── */}
        <div className="overflow-y-auto" style={{ maxHeight: "80vh" }}>

          {/* AI Summary */}
          {ai_summary && (
            <div className="px-5 pt-4">
              <p className="text-xs leading-relaxed" style={{ color: textSecondary }}>
                {ai_summary}
              </p>
            </div>
          )}

          {/* ── Merit Score ── */}
          {merit_score !== null && merit_score !== undefined && (
            <div className="px-5 pt-4 pb-2" style={{ borderBottom: `1px solid ${sectionBorder}` }}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: textMuted }}>
                  Merit Score
                </span>
                <span
                  className="text-2xl font-bold tabular-nums"
                  style={{ color: scoreColor(merit_score) }}
                >
                  {merit_score}
                  <span className="text-sm font-normal" style={{ color: textSecondary }}>/100</span>
                </span>
              </div>

              {score_breakdown && (
                <MeritScoreChart breakdown={score_breakdown} isDark={isDark} />
              )}

              {explanation && (
                <blockquote
                  className="mt-3 text-xs leading-relaxed px-3 py-2 rounded-lg italic"
                  style={{ background: blockquoteBg, borderLeft: "3px solid #f97316", color: textSecondary }}
                >
                  {explanation}
                </blockquote>
              )}
            </div>
          )}

          {/* ── Skills ── */}
          <div className="px-5 pt-4 pb-4" style={{ borderBottom: `1px solid ${sectionBorder}` }}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: textMuted }}>
              Skills
            </p>

            {/* Verified skills */}
            {verifiedSkillEntries.length > 0 && (
              <div className="mb-2">
                <p className="text-xs mb-1.5" style={{ color: textSecondary }}>Verified Skills</p>
                <div className="flex flex-wrap gap-1.5">
                  {verifiedSkillEntries.map((vs) => (
                    <span
                      key={vs.skill_key}
                      className="text-xs font-medium px-2 py-0.5 rounded-full"
                      style={{ background: "rgba(22,163,74,0.12)", color: "#16a34a" }}
                      data-testid={`verified-skill-${vs.skill_key}`}
                    >
                      ✓ {vs.skill_key}
                      {vs.pct !== undefined && vs.pct !== null ? ` ${vs.pct}%` : ""}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Unverified claims */}
            {unverified_flags.length > 0 && (
              <div className="mb-2">
                <p className="text-xs mb-1.5" style={{ color: textSecondary }}>Unverified Claims</p>
                <div className="flex flex-wrap gap-1.5">
                  {unverified_flags.map((flag) => (
                    <span
                      key={flag}
                      className="text-xs font-medium px-2 py-0.5 rounded-full"
                      style={{ background: "rgba(234,179,8,0.12)", color: "#ca8a04" }}
                      data-testid={`unverified-flag-${flag}`}
                    >
                      {flag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Neutral / other skills */}
            {neutralSkills.length > 0 && (
              <div>
                <p className="text-xs mb-1.5" style={{ color: textSecondary }}>Other Skills</p>
                <div className="flex flex-wrap gap-1.5">
                  {neutralSkills.map((skill) => (
                    <span
                      key={skill}
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{
                        background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                        color: textSecondary,
                      }}
                      data-testid={`skill-tag-${skill}`}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Recruiter Notes ── */}
          <div className="px-5 pt-4 pb-4" style={{ borderBottom: `1px solid ${sectionBorder}` }}>
            <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: textMuted }}>
              Recruiter Notes
            </p>
            <textarea
              className="w-full text-sm rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
              rows={4}
              placeholder="Add notes about this candidate..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              style={{
                background: inputBg,
                border: `1px solid ${inputBorder}`,
                color: textPrimary,
              }}
              data-testid="notes-textarea"
            />
            {attritionRisk && (
              <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 10, border: `1px solid ${attritionRisk.risk_level === 'high' ? 'rgba(239,68,68,0.3)' : attritionRisk.risk_level === 'medium' ? 'rgba(249,115,22,0.3)' : 'rgba(5,150,105,0.3)'}`, background: attritionRisk.risk_level === 'high' ? 'rgba(239,68,68,0.08)' : attritionRisk.risk_level === 'medium' ? 'rgba(249,115,22,0.08)' : 'rgba(5,150,105,0.08)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: isDark ? 'rgba(255,255,255,0.4)' : '#94a3b8' }}>Retention Risk</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: attritionRisk.risk_level === 'high' ? '#dc2626' : attritionRisk.risk_level === 'medium' ? '#f97316' : '#059669', textTransform: 'capitalize' }} data-testid="retention-risk-badge">{attritionRisk.risk_level}</span>
                </div>
                {attritionRisk.risk_factors?.filter(f => !f.includes('positive')).slice(0, 2).map((f, i) => (
                  <p key={i} style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.45)' : '#64748b', margin: '2px 0' }}>• {f}</p>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-2">
              <button
                className="text-sm font-medium px-4 py-1.5 rounded-lg transition-opacity hover:opacity-90 active:opacity-75"
                style={{
                  background: "#f97316",
                  color: "#ffffff",
                  boxShadow: "0 2px 8px rgba(249,115,22,0.35)",
                }}
                onClick={() => onSaveNote(noteText)}
                data-testid="save-note-btn"
              >
                Save Note
              </button>
            </div>
          </div>

          {/* ── Stage History ── */}
          {stage_history && stage_history.length > 0 && (
            <div className="px-5 pt-4 pb-5">
              <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: textMuted }}>
                Stage History
              </p>
              <ol className="flex flex-col gap-2.5">
                {stage_history.map((entry, idx) => (
                  <li key={idx} className="flex items-start gap-2.5" data-testid={`stage-history-${idx}`}>
                    <span
                      className="mt-0.5 shrink-0 rounded-full"
                      style={{
                        width: 8,
                        height: 8,
                        background: idx === 0 ? "#f97316" : (isDark ? "rgba(255,255,255,0.25)" : "#cbd5e1"),
                        marginTop: 5,
                      }}
                    />
                    <span className="text-xs leading-relaxed" style={{ color: textSecondary }}>
                      <span className="font-medium" style={{ color: textPrimary }}>
                        {entry.stage}
                      </span>
                      {entry.timestamp && (
                        <span className="ml-2" style={{ color: textMuted }}>
                          {formatDate(entry.timestamp)}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}

        </div>
      </DialogContent>
    </Dialog>
  );
}
